import clsx from "clsx"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMidnightTick } from "@/hooks/useMidnightTick"
import { useGetWithdrawalHistoryQuery } from "@/store/apis/withdrawalAPI"
import { DEFAULT_PAGE_SIZE, Pagination } from "@/ui/Pagination"
import { groupOperationsByDate } from "../../helpers"
import { OperationRow } from "../OperationRow"
import styles from "./OperationsList.module.scss"
import type { IOperationsList } from "./types"

export const OperationsList: FC<IOperationsList> = ({ className, ...rest }) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const midnightTick = useMidnightTick()

  // Use `selectFromResult` to prefer `currentData` (the result for the
  // current page arg) but fall back to `data` (last successful response
  // across arg changes) so the list keeps showing the previous page while
  // the next page is in flight. This replaces the ref-in-render pattern
  // that violated Concurrent React's render purity rule.
  const { source, isFetching } = useGetWithdrawalHistoryQuery(
    { page, limit: DEFAULT_PAGE_SIZE },
    {
      selectFromResult: ({ data, isFetching: fetching, currentData }) => ({
        source: currentData ?? data,
        isFetching: fetching
      })
    }
  )

  const groups = useMemo(
    // `midnightTick` is a deliberate extra dep: bumping it at local midnight
    // recomputes the memo so "today"/"yesterday" labels roll over while the
    // tab is left open. Keep this cheap — groupOperationsByDate runs over
    // the at-most ten visible rows.
    () => groupOperationsByDate(source?.list ?? [], t),
    [source?.list, t, midnightTick]
  )

  const isEmpty = !isFetching && (!source || source.list.length === 0)

  return (
    <div {...rest} className={clsx(styles.OperationsList, className)}>
      <div className={styles.OperationsList_header}>
        <div className={styles.OperationsList_title}>
          {t("wallet.withdraw.history.title")}
        </div>
        {!isEmpty && (
          <Pagination
            page={page}
            total={source?.count ?? 0}
            perPage={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </div>

      {isEmpty ? (
        <div className={styles.OperationsList_empty}>
          {t("wallet.withdraw.history.empty")}
        </div>
      ) : (
        <div
          className={clsx(styles.OperationsList_body, {
            [styles.loading]: isFetching
          })}
        >
          {groups.map((group) => (
            <div key={group.dayKey} className={styles.OperationsList_group}>
              <div className={styles.OperationsList_groupLabel}>
                {group.label}
              </div>
              <div className={styles.OperationsList_groupRows}>
                {group.items.map((tx) => (
                  <OperationRow key={tx._id} transaction={tx} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
